// L'annuaire — cinquième écran migré, et le premier vraiment gros.
//
// Il réunit d'un coup tout ce que les quatre précédents n'avaient qu'à moitié :
// QUATRE abonnements simultanés (amitiés, présence, mes groupes, mes pages),
// une recherche différée, des profils qu'il faut aller chercher à la demande,
// et les suggestions d'amitié — le premier écran où l'algorithme se voit.
//
// Ce qui change vraiment par rapport à l'ancienne version : là-bas, chaque
// abonnement appelait `dessiner()` ET `dessinerSuggestions()` à la main. Quatre
// abonnements × deux fonctions = huit appels à ne pas oublier, et l'oubli ne se
// voyait pas — l'écran affichait simplement quelque chose de périmé. Ici il n'y
// a plus rien à appeler : l'affichage découle de l'état.
//
// Ce qui NE change pas : le classement. `suggererAmis` et `classerRecherche`
// viennent de `js/algorithmes.js`, intacts. Cet écran ne fait que les montrer.

import { useEffect, useMemo, useRef, useState } from "react";
import Store, { etatAmitie } from "../../store.js";
import { suggererAmis, classerRecherche, expliquer, amisEnCommunTexte } from "../../algorithmes.js";
import Avatar from "../Avatar.jsx";
import Certifie from "../Certifie.jsx";
import Icone from "../Icone.jsx";
import BoutonsAmitie from "../BoutonsAmitie.jsx";
import CarteEspace from "../CarteEspace.jsx";
import Vide, { Chargement } from "../Vide.jsx";
import { useAbonnement, useSignaux } from "../hooks.js";

/** Une ligne de l'annuaire : qui c'est, où il est, et quoi faire de lui. */
function LigneMembre({ membre, ctx, relations, enLigne, action }) {
  const lieu = [membre.eglise, membre.ville].filter(Boolean).join(" · ") || "Membre";
  const connecte = enLigne.has(membre.uid);
  /* ⚠️ CETTE LIGNE DESSERT TROIS ÉCRANS D'UN COUP : `LigneMembre` sert aux
     DEMANDES D'AMITIÉ, à MES AMIS et à l'annuaire. Les amis en commun s'y
     placent avant le lieu, et pour une raison locale : dans une communauté
     issue d'une seule église, « ICC Parakou » est vrai de tout le monde et ne
     distingue personne. « 3 amis en commun », si. */
  const communs = amisEnCommunTexte(ctx.signaux?.instantane(), membre.uid);

  return (
    <div className="membre">
      <a className="membre__qui" href={`#/profil/${membre.uid}`}>
        <span className="avatar-enveloppe">
          <Avatar membre={membre} taille={52} anneau={ctx.avecStatut?.has(membre.uid)} />
          {connecte && <span className="point-vert point-vert--sur-avatar" />}
        </span>
        <span>
          <strong>
            {membre.nom}
            <Certifie membre={membre} taille={13} />
          </strong>
          <span className="petit texte-doux">
            {[connecte ? "En ligne" : "", communs, lieu].filter(Boolean).join(" · ")}
          </span>
        </span>
      </a>
      <span className="membre__actions">
        <BoutonsAmitie
          etat={etatAmitie(relations, ctx.moi.uid, membre.uid)}
          uid={membre.uid}
          nom={membre.nom}
          ctx={ctx}
        />
        {action}
      </span>
    </div>
  );
}

/** Le bouton ✉️ : ouvre la conversation avec ce membre, en la créant s'il faut. */
function BoutonMessage({ uid }) {
  const [enCours, setEnCours] = useState(false);
  const ecrire = async () => {
    setEnCours(true);
    try {
      location.hash = `#/messages/${await Store.convos.ensure(uid)}`;
    } finally {
      setEnCours(false);
    }
  };
  return (
    <button className="btn btn--fantome btn--petit" disabled={enCours} onClick={ecrire} aria-label="Écrire à ce membre">
      ✉️
    </button>
  );
}

/**
 * « Vous connaissez peut-être ».
 *
 * Chaque carte porte la RAISON de sa présence — « 3 amis en commun », « même
 * église ». C'est la seule façon honnête de suggérer quelqu'un : le membre voit
 * pourquoi on le lui propose, et peut refuser la proposition d'un geste.
 */
function Suggestions({ liste, ctx, relations, enLigne, onEcarter }) {
  if (!liste.length) return null;

  return (
    <section className="carte">
      <h2 className="titre-page">Vous connaissez peut-être</h2>
      <div className="grille-membres" style={{ marginTop: 12 }}>
        {liste.map(({ membre, raisons }) => {
          const [raison] = expliquer(raisons, 1);
          const connecte = enLigne.has(membre.uid);
          return (
            <div className="membre membre--suggere" key={membre.uid}>
              <a className="membre__qui" href={`#/profil/${membre.uid}`}>
                <span className="avatar-enveloppe">
                  <Avatar membre={membre} taille={52} anneau={ctx.avecStatut?.has(membre.uid)} />
                  {connecte && <span className="point-vert point-vert--sur-avatar" />}
                </span>
                <span>
                  <strong>
                    {membre.nom}
                    <Certifie membre={membre} taille={13} />
                  </strong>
                  <span className="petit texte-doux">{raison || "Suggestion"}</span>
                </span>
              </a>
              <span className="membre__actions">
                <BoutonsAmitie
                  etat={etatAmitie(relations, ctx.moi.uid, membre.uid)}
                  uid={membre.uid}
                  nom={membre.nom}
                  ctx={ctx}
                />
                <button
                  className="btn btn--fantome btn--petit"
                  title="Ne plus me proposer ce membre"
                  aria-label={`Ne plus me proposer ${membre.nom}`}
                  onClick={() => onEcarter(membre.uid)}
                >
                  <Icone nom="fermer" taille={15} />
                </button>
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** Le bandeau des connectés, en haut de l'annuaire. */
function EnLigne({ membres, enLigne }) {
  const connectes = membres.filter((u) => enLigne.has(u.uid));

  if (!connectes.length)
    return <div className="enligne__titre texte-doux">Personne d'autre n'est connecté pour l'instant.</div>;

  return (
    <>
      <div className="enligne__titre">
        <span className="point-vert" /> {connectes.length} membre{connectes.length > 1 ? "s" : ""} en ligne
      </div>
      <div className="enligne__liste">
        {connectes.slice(0, 20).map((u) => (
          <a className="enligne__membre" href={`#/profil/${u.uid}`} title={u.nom} key={u.uid}>
            <span className="avatar-enveloppe">
              <Avatar membre={u} taille={46} />
              <span className="point-vert point-vert--sur-avatar" />
            </span>
            <span className="petit">
              {u.nom.split(" ")[0]}
              <Certifie membre={u} taille={11} />
            </span>
          </a>
        ))}
      </div>
    </>
  );
}

export default function Membres({ ctx }) {
  const [requete, setRequete] = useState("");
  const [membres, setMembres] = useState(null); // null : la première recherche court encore
  const [extras, setExtras] = useState({}); // profils allés chercher à la demande

  const relations = useAbonnement((cb) => Store.amis.subscribe(cb), []);
  // La présence rend désormais { enLigne, vuA } — on ne garde ici que les
  // présents. La date de dernière connexion se lit par `<Presence>`, qui la
  // met en mots au même endroit pour tous les écrans.
  const presence = useAbonnement((cb) => Store.presence.subscribe(cb), null);
  const enLigne = presence?.enLigne || new Set();
  /* Les aperçus « Mes groupes » et « Mes pages » ont quitté cet écran pour le
     Menu, où ils figuraient DÉJÀ : on les voyait donc deux fois, à deux endroits
     qui ne disaient pas la même chose. Membres parle des personnes ; les lieux
     ont leur propre entrée.

     Leurs deux abonnements partent avec eux — c'étaient deux lectures continues
     de Firestore, facturées, pour un contenu affiché ailleurs. */
  const versionSignaux = useSignaux(ctx.signaux);

  // La recherche part 180 ms après la dernière frappe — mais tout de suite au
  // premier affichage : faire attendre l'écran pour rien serait absurde.
  const premiere = useRef(true);
  useEffect(() => {
    let vivant = true;
    const lancer = async () => {
      const liste = (await Store.users.search(requete)).filter((u) => u.uid !== ctx.moi.uid);
      if (!vivant) return;
      // l'annuaire nourrit le classement : plus il connaît de profils, plus il est juste
      ctx.signaux?.apprendreMembres(liste);
      setMembres(liste);
    };
    const delai = premiere.current ? 0 : 180;
    premiere.current = false;
    const t = setTimeout(lancer, delai);
    return () => {
      vivant = false;
      clearTimeout(t);
    };
  }, [requete, ctx]);

  const listeMembres = membres || [];

  const demandes = relations.filter((r) => r.etat === "attente" && r.vers === ctx.moi.uid);

  // Quelqu'un peut m'envoyer une demande sans figurer dans la recherche
  // courante — parce que je cherche « Marie » et qu'il s'appelle Jean. Il faut
  // alors aller chercher son profil, sinon sa demande s'afficherait « Membre ».
  useEffect(() => {
    const manquants = demandes
      .map((r) => r.de)
      .filter((id) => !listeMembres.some((m) => m.uid === id) && !extras[id]);
    if (!manquants.length) return;
    let vivant = true;
    (async () => {
      const trouves = {};
      for (const id of manquants) trouves[id] = (await ctx.profil(id)) || { uid: id, nom: "Membre" };
      if (vivant) setExtras((v) => ({ ...v, ...trouves }));
    })();
    return () => {
      vivant = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demandes.map((r) => r.de).join(","), membres]);

  const membreConnu = (uid) =>
    listeMembres.find((m) => m.uid === uid) || extras[uid] || { uid, nom: "Membre" };

  // Le calcul est dans algorithmes.js ; ici on ne fait que l'afficher. Refait
  // quand l'annuaire change ou quand les signaux ont appris quelque chose.
  const suggestions = useMemo(
    () => (ctx.signaux ? suggererAmis(listeMembres, ctx.signaux.instantane(), { combien: 8 }) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [membres, versionSignaux]
  );

  // Mes amis d'abord, puis les autres — classés, pas alphabétiques : un frère
  // de la même église passe avant un inconnu.
  const { amis, autres } = useMemo(() => {
    const classes = ctx.signaux
      ? classerRecherche(listeMembres, requete, ctx.signaux.instantane())
      : listeMembres;
    const idsAmis = new Set(
      relations.filter((r) => r.etat === "acceptee").map((r) => r.membres.find((m) => m !== ctx.moi.uid))
    );
    return {
      amis: classes.filter((u) => idsAmis.has(u.uid)),
      autres: classes.filter((u) => !idsAmis.has(u.uid)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membres, requete, relations, versionSignaux]);

  const ecarter = (uid) => ctx.signaux?.ecarter(uid); // les signaux prévoient : useSignaux redessine

  return (
    <div className="colonne">
      {demandes.length > 0 && (
        <section className="carte">
          <h2 className="titre-page">
            Demandes d'amitié <span className="compteur">{demandes.length}</span>
          </h2>
          <div className="grille-membres" style={{ marginTop: 12 }}>
            {demandes.map((r) => (
              <LigneMembre
                key={r.de}
                membre={membreConnu(r.de)}
                ctx={ctx}
                relations={relations}
                enLigne={enLigne}
              />
            ))}
          </div>
        </section>
      )}

      <Suggestions
        liste={suggestions}
        ctx={ctx}
        relations={relations}
        enLigne={enLigne}
        onEcarter={ecarter}
      />

      {/* Le titre « Membres de la communauté » est parti avec la liste qu'il
          annonçait : cette carte ne porte plus que les amis en ligne et le
          champ de recherche, qui se passent tous deux de présentation. */}
      <section className="carte">
        <div className="enligne">
          <EnLigne membres={listeMembres} enLigne={enLigne} />
        </div>
        <input
          className="champ"
          type="search"
          placeholder="Rechercher un nom, une ville, une église…"
          autoComplete="off"
          value={requete}
          onChange={(e) => setRequete(e.target.value)}
        />
      </section>

      {/* ⚠️ « Membres » est devenu « Mes amis » hors recherche — voir la note du
          bloc « Autres membres » plus bas. Le titre annonçait toute la
          communauté ; il n'y a plus qu'un cercle. */}
      <h2 className="titre-section">
        {requete.trim() ? "Résultats" : `Mes amis${amis.length ? ` (${amis.length})` : ""}`}
      </h2>

      {membres === null ? (
        <Chargement />
      ) : listeMembres.length ? (
        <>
          {amis.length > 0 && (
            <>
              {/* Le sous-titre ne sert qu'en recherche, où les deux groupes se
                  suivent et doivent se distinguer. Hors recherche, le titre
                  au-dessus le dit déjà. */}
              {requete.trim() && <h3 className="sous-titre">Mes amis ({amis.length})</h3>}
              <div className="grille-membres">
                {amis.map((u) => (
                  <LigneMembre
                    key={u.uid}
                    membre={u}
                    ctx={ctx}
                    relations={relations}
                    enLigne={enLigne}
                    action={<BoutonMessage uid={u.uid} />}
                  />
                ))}
              </div>
            </>
          )}
          {/* ⚠️ « AUTRES MEMBRES » NE S'AFFICHE PLUS QU'EN RECHERCHE.
              C'était la liste de TOUS LES INSCRITS de la plateforme, déroulée à
              l'ouverture de l'écran. Elle contredisait tout le reste : depuis la
              cinquième passe, Koinonia n'est pas une place publique — on ne voit
              que son cercle. Un nouveau venu ne voit rien du fil, rien des
              statuts, personne en ligne… et parcourait pourtant l'annuaire
              entier d'un coup de pouce.
              Retiré le 14 août 2026 à la demande de Josaphat.

              ⚠️ MAIS PAS SUPPRIMÉ, et c'est délibéré : c'est ici que
              s'affichent les RÉSULTATS DE RECHERCHE. Chercher quelqu'un est un
              geste volontaire — on tape un nom qu'on connaît déjà. Sans ce
              bloc, on ne pourrait plus ajouter personne qui ne soit pas
              proposé. */}
          {requete.trim() && autres.length > 0 && (
            <>
              {amis.length > 0 && <h3 className="sous-titre">Autres membres</h3>}
              <div className="grille-membres">
                {autres.map((u) => (
                  <LigneMembre
                    key={u.uid}
                    membre={u}
                    ctx={ctx}
                    relations={relations}
                    enLigne={enLigne}
                    action={<BoutonMessage uid={u.uid} />}
                  />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <Vide symbole="🔍" titre="Aucun membre trouvé" texte="Essayez un autre nom ou une autre ville." />
      )}
    </div>
  );
}
